import { ageBandLabel } from "./familyProfileChat";
import {
  DEFAULT_KINDERGARTEN_WEEKDAYS,
  SETTING_LABELS,
  SETTING_OPTIONS,
  ageBandFromBirthYm,
  audienceAskChipLabels,
  childDisplayName,
  inferSetting,
  isKindergartenChild,
  isKitaChild,
  matchProfileChild,
  normalizeChildren,
} from "./familyChildren";
import { formatKitaWeekdaysLabel } from "./kitaRelevance";

export const KIDS_FLOW_TEXT = {
  askNameOne: (who) => `Wie heisst ${who}? Schreib den Vornamen.`,
  namesSkip: "Ohne Namen",
  namesHint: "Schreib den Vornamen — oder überspringe.",
  askSetting: (who) => `${who}: KiTa oder Kindergarten? Damit die Tage stimmen.`,
  recapIntro: "So habe ich euch notiert:",
  recapEmpty:
    "Ich habe noch keine Kinder notiert. Du kannst ein Alter antippen — oder später nachtragen.",
  recapAsk: "Etwas ändern?",
  pickChildDays: "Für wen die Tage ändern?",
  pickChildName: "Welches Kind umbenennen?",
  pickChildSetting: "Für wen KiTa oder Kindergarten ändern?",
  addAge: "Wie alt ist das Kind?",
  saved: "Merci — gespeichert.",
  closed: "Alles klar.",
  whoAsk: (title, date) =>
    `Für wen gilt ${title || "das"}${date ? ` (${date})` : ""}?`,
  whoBoth: "Beide",
  whoNone: "Niemand",
  whoUnknown: "Weiss nicht",
  whoNoneSaved: (title) =>
    `Alles klar — ${title || "das"} blende ich aus.`,
  whoHint: "Tippe ein Kind, Beide, Niemand oder Weiss nicht.",
};

export const KIDS_AUDIENCE_TEXT = {
  ask: (who) =>
    `In der Mail stehen Gruppen oder Jahrgänge. Was gilt für ${who}? Tippe an.`,
  all: "Gilt für alle",
  unknown: "Weiss nicht",
  save: "Speichern",
  hint: "Tippe die Gruppen oder Jahrgänge an, die zu euch gehören — oder überspringe.",
  saved: (who, labels) =>
    labels.length
      ? `Merci — ${who}: ${labels.join(", ")}.`
      : `Alles klar — ich merke mir das für ${who}.`,
};

export const KIDS_DAYS_TEXT = {
  askKita: (who) =>
    `An welchen Tagen ist ${who} in der KiTa? Tippe die Tage an.`,
  askKiga: (who) =>
    `An welchen Tagen ist ${who} im Kindergarten? Standard ist Mo–Fr — du kannst das anpassen.`,
  askHort: (who) =>
    `Hat ${who} Hort? Tippe die Tage an — oder überspringe, wenn nicht.`,
  savedKita: (who, days) =>
    `Merci — ${who} KiTa ${formatKitaWeekdaysLabel(days)}.`,
  savedKiga: (who, days) =>
    `Merci — ${who} Kindergarten ${formatKitaWeekdaysLabel(days) || "Mo–Fr"}.`,
  savedHort: (who, days) =>
    days.length
      ? `Merci — ${who} Hort ${formatKitaWeekdaysLabel(days)}.`
      : `Alles klar — kein Hort für ${who} notiert.`,
  skippedHort: (who) => `Alles klar — kein Hort für ${who}.`,
};

export function isHortTopicQuestion(text) {
  const t = String(text || "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (/steht an|frist|termin/.test(t) && !/hort/.test(t)) return false;
  return /\bhort\b|mittagstisch|nachmittagshort|tageshort|ferienhort/.test(t);
}

/**
 * True when the parent wants to see or change kids / Betreuungstage (not schedule recall).
 * @param {string} text
 */
export function isKidsMemoryQuestion(text) {
  const t = String(text || "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (/steht an|welche frist|welche termin|was hat /.test(t)) return false;
  return (
    /kita[- ]tage|betreuungstage|hort[- ]tage/.test(t) ||
    /unsere kinder|eure kinder|welche kinder|kinder andern|kinder anpassen/.test(
      t,
    ) ||
    /kind hinzufugen|zweites kind|2\. kind/.test(t) ||
    /was weiss(?:t)? du uber/.test(t) ||
    /wie alt sind (?:die |unsere |eure )?kinder/.test(t) ||
    /kindergarten oder kita|kita oder kindergarten/.test(t)
  );
}

export function formatChildRecapLine(child, index) {
  const who = childDisplayName(child, index);
  const setting = inferSetting(child);
  const parts = [who];
  if (isKitaChild(child)) {
    parts.push(
      child.kita_weekdays?.length
        ? `KiTa ${formatKitaWeekdaysLabel(child.kita_weekdays)}`
        : SETTING_LABELS.kita,
    );
  } else if (isKindergartenChild(child)) {
    const kigaDays = child.kita_weekdays?.length
      ? child.kita_weekdays
      : DEFAULT_KINDERGARTEN_WEEKDAYS;
    parts.push(`Kindergarten ${formatKitaWeekdaysLabel(kigaDays)}`);
    if (child.hort_weekdays?.length) {
      parts.push(`Hort ${formatKitaWeekdaysLabel(child.hort_weekdays)}`);
    }
  } else if (setting) {
    parts.push(SETTING_LABELS[setting]);
  } else {
    const band = ageBandFromBirthYm(child?.birth_ym);
    const bandLabel = ageBandLabel(band);
    if (bandLabel) parts.push(bandLabel);
  }
  if (child.audience?.length) {
    parts.push(child.audience.join(", "));
  }
  return parts.join(" · ");
}

export function buildKidsRecapText(children, { quartier, postalCode } = {}) {
  const list = normalizeChildren(children);
  if (list.length === 0) return KIDS_FLOW_TEXT.recapEmpty;
  const lines = list.map((child, i) => formatChildRecapLine(child, i));
  let where = "";
  if (quartier) where = `\nWetter für ${quartier}.`;
  else if (postalCode) where = `\nWetter für PLZ ${postalCode}.`;
  return `${KIDS_FLOW_TEXT.recapIntro}\n${lines.join("\n")}${where}\n\n${KIDS_FLOW_TEXT.recapAsk}`;
}

export function kidsRecapOptions(makeId) {
  return [
    { id: makeId(), label: "Tage ändern", value: "days", kind: "kids-days" },
    { id: makeId(), label: "KiTa / Kindergarten", value: "setting", kind: "kids-setting" },
    { id: makeId(), label: "Kind hinzufügen", value: "add", kind: "kids-add" },
    { id: makeId(), label: "Name", value: "name", kind: "kids-name" },
    { id: makeId(), label: "Passt so", value: "ok", kind: "kids-ok" },
  ];
}

export function settingOptions(makeId) {
  return SETTING_OPTIONS.map((o) => ({
    id: makeId(),
    label: o.label,
    value: o.value,
    kind: "child-setting",
  }));
}

export function childPickOptions(children, makeId, kind) {
  return normalizeChildren(children).map((child, i) => ({
    id: makeId(),
    label: formatChildRecapLine(child, i),
    value: child.id,
    kind,
  }));
}

export function whoAskOptions(children, makeId) {
  const named = normalizeChildren(children).filter((c) => c.name);
  return [
    ...named.map((child, i) => ({
      id: makeId(),
      label: childDisplayName(child, i),
      value: child.name,
      kind: "who-child",
    })),
    {
      id: makeId(),
      label: named.length > 2 ? "Alle" : KIDS_FLOW_TEXT.whoBoth,
      value: "both",
      kind: "who-both",
    },
    { id: makeId(), label: KIDS_FLOW_TEXT.whoNone, value: "none", kind: "who-none" },
    { id: makeId(), label: KIDS_FLOW_TEXT.whoUnknown, value: "unknown", kind: "who-unknown" },
  ];
}

function foldWhoText(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Parse a typed answer to «Für wen gilt …?».
 * @param {string} text
 * @param {unknown} children
 * @returns {{ kind: "child"|"both"|"none"|"unknown", childValue?: string } | null}
 */
export function parseWhoAnswer(text, children) {
  const t = foldWhoText(text);
  if (!t) return null;
  if (/weiss nicht|keine ahnung|^unknown$/.test(t)) {
    return { kind: "unknown" };
  }
  if (
    /^(niemand|niemanden|keiner|keine|keins|nobody)$/.test(t) ||
    /^(fuer |fur )?niemand(en)?$/.test(t) ||
    /kein(e|es)? kind/.test(t) ||
    /keiner von|keine von/.test(t) ||
    /gehort nicht dazu|gehoert nicht dazu/.test(t) ||
    /nicht relevant/.test(t)
  ) {
    return { kind: "none" };
  }
  if (/^(beide|alle)$/.test(t)) {
    return { kind: "both" };
  }
  const named = normalizeChildren(children).filter((c) => c.name);
  const matched = matchProfileChild(t, named);
  if (matched?.name) return { kind: "child", childValue: matched.name };
  for (const child of named) {
    const n = foldWhoText(child.name);
    if (n && (t === n || t.includes(n))) {
      return { kind: "child", childValue: child.name };
    }
  }
  return null;
}

/**
 * @param {unknown} labels
 * @param {unknown} selected
 * @param {(id?: string) => string} makeId
 */
export function audienceAskOptions(labels, selected, makeId) {
  const chips = audienceAskChipLabels(labels);
  const selectedSet = new Set(
    (Array.isArray(selected) ? selected : []).map((s) => String(s).toLowerCase()),
  );
  return [
    ...chips.map((label) => ({
      id: makeId(),
      label,
      value: label,
      kind: "audience-label",
      selected: selectedSet.has(String(label).toLowerCase()),
    })),
    { id: makeId(), label: KIDS_AUDIENCE_TEXT.save, value: "save", kind: "audience-done" },
    { id: makeId(), label: KIDS_AUDIENCE_TEXT.all, value: "all", kind: "audience-all" },
    { id: makeId(), label: KIDS_AUDIENCE_TEXT.unknown, value: "unknown", kind: "audience-unknown" },
  ];
}

/**
 * Parse a typed answer to the Gruppe/Jahr question.
 * @param {string} text
 * @param {string[]} labels
 * @returns {{ kind: "labels"|"all"|"unknown", labels?: string[] } | null}
 */
export function parseAudienceAnswer(text, labels) {
  const t = foldWhoText(text);
  if (!t) return null;
  if (/weiss nicht|keine ahnung|^unknown$/.test(t)) return { kind: "unknown" };
  if (/gilt fuer alle|gilt fur alle|fuer alle|fur alle|^alle$/.test(t)) {
    return { kind: "all" };
  }
  const pool = Array.isArray(labels) ? labels : [];
  const matched = pool.filter((label) => {
    const n = foldWhoText(label);
    return n && (t === n || t.includes(n));
  });
  if (matched.length > 0) return { kind: "labels", labels: matched };
  return null;
}
